import { Link, useParams } from 'react-router';
import { api } from '../../shared/api/client';
import { Button, Card, ErrorState, Loading, Logo } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { formatPrice, INTERVAL_LABELS } from './format';

export type HistoryItem = { id: string; kind: string; at: string; amountCents: number | null; currency: string | null; periodEnd: string | null; interval: string | null; provider: string | null };
const KIND: Record<string, string> = {
  trial_started: 'Inicio de la prueba',
  payment_succeeded: 'Pago recibido',
  payment_failed: 'Pago no realizado',
  payment_mismatch: 'Pago por revisar',
  interval_changed: 'Cambio de frecuencia de pago',
  terms_changed: 'Cambio de condiciones',
  addon_added: 'Extra agregado',
  addon_removed: 'Extra quitado',
  plan_changed: 'Cambio de plan',
  canceled: 'Suscripción cancelada',
};
const PROVIDER: Record<string, string> = { mercadopago: 'Mercado Pago', stripe: 'Stripe', simulated: 'Simulado' };
const day = (iso: string) => new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' });

/** Historial de la suscripción: pagos con su recibo, y cambios. */
export function BillingHistory({ churchId, currency }: { churchId: string; currency: string }) {
  const data = useAsync((signal) => api.get<{ items: HistoryItem[] }>(`/saas/churches/${churchId}/subscription/history`, signal).then((r) => r.items), [churchId]);
  if (data.status === 'loading') return <Loading />;
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  if (data.data.length === 0) return null;
  return (
    <Card title="Historial">
      <ul className="list">
        {data.data.map((h) => (
          <li key={h.id} className="list__item">
            <span className="agenda__body">
              <strong>{KIND[h.kind] ?? h.kind}</strong>
              <span className="small muted">
                {day(h.at)}
                {h.amountCents !== null ? ` · ${formatPrice(h.amountCents, h.currency ?? currency)}` : ''}
                {h.interval ? ` · ${INTERVAL_LABELS[h.interval] ?? h.interval}` : ''}
                {h.periodEnd ? ` · cubre hasta el ${day(h.periodEnd)}` : ''}
              </span>
            </span>
            {h.kind === 'payment_succeeded' ? (
              <Link className="btn btn--ghost" to={`/app/facturacion/recibo/${h.id}`} target="_blank">
                Recibo
              </Link>
            ) : null}
          </li>
        ))}
      </ul>
      <p className="small muted">El recibo es un comprobante de pago. Para factura fiscal, escríbenos.</p>
    </Card>
  );
}

/** Recibo imprimible (o para guardar como PDF) de un pago de la suscripción. */
export function ReceiptPage() {
  const { eventId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const data = useAsync((signal) => (churchId ? api.get<{ items: HistoryItem[] }>(`/saas/churches/${churchId}/subscription/history`, signal).then((r) => r.items.find((h) => h.id === eventId) ?? null) : Promise.resolve(null)), [churchId, eventId]);
  return (
    <div className="print-sheet">
      <div className="print-sheet__bar no-print">
        <Button onClick={() => window.print()}>Imprimir o guardar PDF</Button>
      </div>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} /> : null}
      {data.status === 'ready' && data.data ? (
        <>
          <header className="print-sheet__head">
            <Logo />
            <div>
              <h1>Recibo de pago</h1>
              <p>Folio {data.data.id.slice(0, 8).toUpperCase()}</p>
            </div>
          </header>
          <dl className="details">
            <dt>Iglesia</dt>
            <dd>{me.activeContext?.churchName}</dd>
            <dt>Fecha</dt>
            <dd>{day(data.data.at)}</dd>
            <dt>Concepto</dt>
            <dd>Suscripción KEORA{data.data.interval ? ` · ${INTERVAL_LABELS[data.data.interval] ?? ''}` : ''}{data.data.periodEnd ? ` · cubre hasta el ${day(data.data.periodEnd)}` : ''}</dd>
            <dt>Monto</dt>
            <dd>
              <strong>{data.data.amountCents !== null ? formatPrice(data.data.amountCents, data.data.currency ?? 'MXN') : '—'}</strong>
            </dd>
            {data.data.provider ? (
              <>
                <dt>Medio</dt>
                <dd>{PROVIDER[data.data.provider] ?? data.data.provider}</dd>
              </>
            ) : null}
          </dl>
          <p className="small">Este recibo es un comprobante de pago; no es una factura fiscal.</p>
        </>
      ) : data.status === 'ready' ? (
        <p>No encontramos ese pago.</p>
      ) : null}
    </div>
  );
}
